// DEPENDENCIES ---------------------------------------------------------------- //

import { Moves } from '@gql/base-types';

import { css, keyframes } from '@emotion/css';
import { Theme, useTheme, Typography } from '@mui/material';

// HELPERS ---------------------------------------------------------------- //

const getSizePadding = (size: `sm` | `md` | `lg`) => {
  switch (size) {
    case `sm`:
      return `8px`;
    case `md`:
      return `10px`;
    case `lg`:
      return `12px`;
  }
};

const getSizeFont = (size: `sm` | `md` | `lg`) => {
  switch (size) {
    case `sm`:
      return `10px`;
    case `md`:
      return `14px`;
    case `lg`:
      return `18px`;
  }
};

const getSizeGap = (size: `sm` | `md` | `lg`) => {
  switch (size) {
    case `sm`:
      return `16px`;
    case `md`:
      return `20px`;
    case `lg`:
      return `24px`;
  }
};

const getSizeContentWidth = (size: `sm` | `md` | `lg`) => {
  switch (size) {
    case `sm`:
      return `12px`;
    case `md`:
      return `16px`;
    case `lg`:
      return `20px`;
  }
};

const getSizeContentOffset = (size: `sm` | `md` | `lg`) => {
  switch (size) {
    case `sm`:
      return `-6px`;
    case `md`:
      return `-8px`;
    case `lg`:
      return `-10px`;
  }
};

// PROGRESS LOGIC ---------------------------------------------------------------- //// Status progression for ride moves:
// pending - Move pending execution
// ready_to_book - Ready for ridehail booking
// booked - Ridehail booked, driver enroute to pickup
// driver_arrived - Driver has arrived at pickup location
// in_progress - Passenger is in vehicle, ride is active
// completed - Move completed successfully
// cancelled - Move cancelled
// failed - Move failed


const getRideProgressStageFromStatus = (ridehailStatus?: string | null, rideMove?: Moves): number => {
  // Prioritize ridehail status over move status
  const status = (ridehailStatus || rideMove?.status)?.toLowerCase();

  if (!status) return 0;

  // Handle cancelled/failed as stage 0 (preparing)
  if (status === 'cancelled' || status === 'failed') return 0;

  // Stage 2: Ridehail Completed
  if (status === 'completed') return 2;

  // Stage 1: Ridehail In Progress (including driver states)
  if (status === 'in_progress' || status === 'driver_assigned' || status === 'driver_arrived') return 1;

  // Stage 0: Preparing (pending, ready_to_book, booked)
  return 0;
};

const getCurrentActiveStage = (ridehailStatus?: string | null, rideMove?: Moves): number => {
  // Prioritize ridehail status over move status
  const status = (ridehailStatus || rideMove?.status)?.toLowerCase();

  if (!status) return 0;

  const currentStage = getRideProgressStageFromStatus(ridehailStatus, rideMove);

  // If cancelled or failed, pulse stage 0
  if (status === 'cancelled' || status === 'failed') return 0;

  // If completed, no pulsing
  if (status === 'completed') return -1;

  // Pulse the current stage if it's in progress
  return currentStage;
};

// TYPES ---------------------------------------------------------------- //

interface RideProgressTrackerProps {
  rideMove?: Moves;
  ridehailStatus?: string | null;
  size?: `sm` | `md` | `lg`;
}

// COMPONENT ---------------------------------------------------------------- //

const RideProgressTracker = ({ rideMove, ridehailStatus, size = `md` }: RideProgressTrackerProps) => {
  const theme = useTheme();
  const progressStage = getRideProgressStageFromStatus(ridehailStatus, rideMove);
  const activeStage = getCurrentActiveStage(ridehailStatus, rideMove);
  const cls = useStyles(theme, { size, progressStage, activeStage });

  if (!rideMove) return null;

  return (
    <div className={cls.rideProgress}>
      {/* Preparing - larger segment */}
      <div className={cls.preparing}>
        <Typography sx={{ lineHeight: 1, fontSize: getSizeFont(size), fontWeight: 600 }}>
          Ridehail Pending
        </Typography>
      </div>

      {/* Ridehail In Progress - larger segment */}
      <div className={cls.inProgress}>
        <Typography sx={{ lineHeight: 1, fontSize: getSizeFont(size), fontWeight: 600 }}>
          Ridehail In Progress
        </Typography>
      </div>

      {/* Ridehail Completed - largest segment */}
      <div className={cls.completed}>
        <Typography sx={{ lineHeight: 1, fontSize: getSizeFont(size), fontWeight: 600 }}>
          Ridehail Completed
        </Typography>
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme, props?: any) => {
  const pulse = keyframes`
    0% {
      background-color: ${theme?.palette?.primary?.main};
    }
    50% {
      background-color: ${theme?.palette?.primary?.light};
    }
    100% {
      background-color: ${theme?.palette?.primary?.main};
    }
  `;

  const getSegmentStyles = (stageIndex: number, flex: number, borderRadius: string) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: ${flex};
    padding: ${getSizePadding(props?.size)};
    border-radius: ${borderRadius};
    color: ${theme?.palette?.common?.white};
    background-color: ${props?.progressStage >= stageIndex
      ? theme?.palette?.primary?.main
      : theme?.palette?.grey?.[200]};
    animation: ${props?.activeStage === stageIndex ? `${pulse} 1s infinite` : `none`};
    ${stageIndex > 0 ? `
      ::before {
        content: '';
        position: absolute;
        top: 0px;
        left: ${getSizeContentOffset(props?.size)};
        width: ${getSizeContentWidth(props?.size)};
        height: 100%;
        transform: skewX(-24deg);
        background-color: ${props?.progressStage >= stageIndex
          ? theme?.palette?.primary?.main
          : theme?.palette?.grey?.[200]};
        animation: ${props?.activeStage === stageIndex ? `${pulse} 1s infinite` : `none`};
      }
    ` : ''}
    ${stageIndex < 2 ? `
      ::after {
        content: '';
        position: absolute;
        top: 0px;
        right: ${getSizeContentOffset(props?.size)};
        width: ${getSizeContentWidth(props?.size)};
        height: 100%;
        transform: skewX(-24deg);
        background-color: ${props?.progressStage >= stageIndex
          ? theme?.palette?.primary?.main
          : theme?.palette?.grey?.[200]};
        animation: ${props?.activeStage === stageIndex ? `${pulse} 1s infinite` : `none`};
      }
    ` : ''}
  `;

  return {
    rideProgress: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${getSizeGap(props?.size)};
      margin-bottom: 16px;
    `,

    preparing: getSegmentStyles(0, 1.8, '64px 0px 0px 64px'), // Larger, left rounded

    inProgress: getSegmentStyles(1, 1.8, '0px'), // Larger

    completed: getSegmentStyles(2, 2.2, '0px 64px 64px 0px'), // Largest, right rounded
  };
};

// EXPORT ---------------------------------------------------------------- //

export default RideProgressTracker;